import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { FieldSeparator } from '@bliks-io/ui';
import {
  FormInput,
  FormSystemCategorySelect,
  FormTextarea,
  FormUserSelect,
  useFieldErrorMessage
} from '@/components/form/rhf';
import { FormFieldInput } from '@/components/form/FormFieldInput';
import {
  AddSystemRequestSchema,
  type AddSystemRequest,
  type UpdateSystemRequest,
  type System
} from '@bliks-io/contracts';
import { extractEntityId } from '@/lib/form-utils';
import { parseSeatLimit } from '@/lib/seat-limits';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { logger } from '@/lib/logger';
import { SystemLogoUpload } from './SystemLogoUpload';
import { uploadToPresignedUrl } from '@/lib/file-utils';
import {
  useRequestSystemLogoUploadUrl,
  useConfirmSystemLogoUpload,
  useDeleteSystemLogo
} from '@/api/mutations/useSystemLogoUpload';

interface SystemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    data: AddSystemRequest | UpdateSystemRequest
  ) => Promise<Pick<System, '_id'> | undefined> | undefined;
  system?: System;
  /**
   * Seed values for a *create* (no `system`) — e.g. prefilling from an approved
   * entity suggestion. Merged over the empty create defaults; `isEditing` stays
   * false so the form keeps create copy and submits the full payload.
   */
  defaultValuesOverride?: Partial<AddSystemRequest>;
  /** Title override; defaults to the internal create/edit copy. */
  title?: string;
  /** Description override; defaults to the internal create/edit copy. */
  description?: string;
  /** Submit-button label override; defaults to FormDialog's create/save copy. */
  submitLabel?: string;
  /**
   * Fires only after a successful *create* — once the new entity exists and any
   * follow-up logo upload has been attempted (a failed upload is non-fatal: the
   * entity already exists, so the flow proceeds with a warning toast). Lets a
   * host (e.g. the approve flow) defer its close + toast until then. It does
   * not fire when the submit handler resolves without a created entity (e.g.
   * an error the host swallowed), so the host needs no separate success flag.
   * Receives the created entity, so a host can act on its id.
   */
  onSuccess?: (created: Pick<System, '_id'>) => void;
}

/**
 * Resolve the form's seed values: an explicit create-time `override` wins, then
 * the edited `system`, then empty. Kept out of the component so its branch
 * count doesn't inflate the component's cyclomatic complexity.
 */
function buildSystemDefaultValues(
  system: System | undefined,
  override: Partial<AddSystemRequest> | undefined
): AddSystemRequest {
  return {
    name: override?.name ?? system?.name ?? '',
    description: override?.description ?? system?.description ?? '',
    systemOwner: override?.systemOwner ?? extractEntityId(system?.systemOwner) ?? '',
    category: override?.category ?? system?.category ?? '',
    url: override?.url ?? system?.url ?? '',
    licenceSeats: override?.licenceSeats ?? system?.licenceSeats ?? undefined
  };
}

/**
 * Form dialog for creating/editing systems
 * Supports logo upload and system owner assignment
 */
export function SystemFormDialog({
  open,
  onOpenChange,
  onSubmit,
  system,
  defaultValuesOverride,
  title,
  description,
  submitLabel,
  onSuccess
}: SystemFormDialogProps) {
  const { t } = useTranslation();
  const fieldErrorMessage = useFieldErrorMessage();
  const isEditing = !!system;

  // Logo upload state — separate from form state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  const requestLogoUrl = useRequestSystemLogoUploadUrl();
  const confirmLogoUpload = useConfirmSystemLogoUpload();
  const deleteSystemLogo = useDeleteSystemLogo();

  const defaultValues = buildSystemDefaultValues(system, defaultValuesOverride);

  useEffect(
    function resetLogoStateWhenDialogOpens() {
      if (open) {
        setLogoFile(null);
        setRemoveLogo(false);
      }
    },
    [open]
  );

  // Logo add/change/remove is tracked outside the RHF form; `extraDirty` below
  // feeds that into FormDialog so its edit gate and dirty-subset extraction see
  // a logo-only change. On edit, `data` therefore already is the dirty subset.
  const handleSubmit = async (data: AddSystemRequest) => {
    if (isEditing && system) {
      await handleLogoOperationsForEdit(system._id);
    }

    const createdOrUpdatedSystem = await onSubmit(data);

    if (!isEditing && createdOrUpdatedSystem) {
      try {
        await handleLogoUploadForCreate(createdOrUpdatedSystem._id);
      } catch (logoUploadError) {
        // The system already exists at this point, so a failed logo upload
        // must not strand the flow — re-submitting would create (or, in the
        // approve flow, re-approve) again. Warn and move on; the logo can be
        // added later by editing the system.
        logger.error('System logo upload after create failed:', logoUploadError);
        toast.warning(t('organization.systems.logoUploadFailed'));
      }
      // Signal completion only after the create has resolved and the logo
      // upload has been attempted, so a host approve-flow can close + toast.
      // Gated on a created entity, so a host whose adapter swallows its own
      // errors (resolving with no entity) never sees a false "success".
      onSuccess?.(createdOrUpdatedSystem);
    }
  };

  const handleLogoFileChange = (file: File | null) => {
    setLogoFile(file);
    if (file) setRemoveLogo(false);
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setRemoveLogo(true);
  };

  const handleLogoOperationsForEdit = async (systemId: string) => {
    if (removeLogo && system?.logo) {
      await deleteSystemLogo.mutateAsync({ systemId });
    }

    if (logoFile) {
      const presignedData = await requestLogoUrl.mutateAsync({ systemId });
      await uploadToPresignedUrl(presignedData.url, presignedData.fields, logoFile);
      await confirmLogoUpload.mutateAsync({
        systemId,
        key: presignedData.key,
        mimeType: logoFile.type
      });
    }
  };

  const handleLogoUploadForCreate = async (systemId: string) => {
    if (!logoFile) return;

    const presignedData = await requestLogoUrl.mutateAsync({ systemId });
    await uploadToPresignedUrl(presignedData.url, presignedData.fields, logoFile);
    await confirmLogoUpload.mutateAsync({
      systemId,
      key: presignedData.key,
      mimeType: logoFile.type
    });
  };

  const isLogoUploading =
    requestLogoUrl.isPending || confirmLogoUpload.isPending || deleteSystemLogo.isPending;

  return (
    <FormDialog<AddSystemRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={
        title ??
        (isEditing ? t('organization.systems.editSystem') : t('organization.systems.create'))
      }
      description={
        description ??
        (isEditing
          ? t('organization.systems.editSystemDescription')
          : t('organization.systems.createSystemDescription'))
      }
      icon={ENTITY_ICONS.system}
      schema={AddSystemRequestSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isEditing={isEditing}
      submitLabel={submitLabel}
      extraDirty={logoFile !== null || removeLogo}
    >
      {({ form }) => (
        <div className="flex flex-col gap-8">
          {/* Logo Upload Section */}
          <div className="flex flex-col gap-4">
            <SystemLogoUpload
              logo={!removeLogo && system ? system.logo : undefined}
              file={logoFile}
              systemName={system?.name || ''}
              loading={isLogoUploading}
              onFileChange={handleLogoFileChange}
              onRemove={handleLogoRemove}
            />
          </div>

          {/* Basic Information Section */}
          <div className="flex flex-col gap-4">
            <FieldSeparator>
              {t('organization.systems.basicInformation', 'Basic Information')}
            </FieldSeparator>

            <FormInput
              control={form.control}
              name="name"
              label={t('common.Name')}
              placeholder={t('organization.systems.namePlaceholder')}
              required
            />

            <FormTextarea
              control={form.control}
              name="description"
              label={t('common.Description')}
              placeholder={t('organization.systems.descriptionPlaceholder')}
            />
          </div>

          {/* System Owner Section */}
          <div className="flex flex-col gap-4">
            <FieldSeparator>{t('organization.systems.ownership', 'Ownership')}</FieldSeparator>

            <FormUserSelect
              control={form.control}
              name="systemOwner"
              label={t('organization.systems.systemOwner', 'System Owner')}
              placeholder={t(
                'organization.systems.systemOwnerPlaceholder',
                'Select system owner...'
              )}
              required
            />
          </div>

          {/* System Details Section */}
          <div className="flex flex-col gap-4">
            <FieldSeparator>
              {t('organization.systems.systemDetails', 'System Details')}
            </FieldSeparator>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormSystemCategorySelect
                control={form.control}
                name="category"
                label={t('organization.systems.category')}
                placeholder={t('organization.systems.categoryPlaceholder')}
              />

              <FormInput
                control={form.control}
                name="url"
                label={t('organization.systems.url')}
                placeholder={t('organization.systems.urlPlaceholder')}
              />

              <Controller
                control={form.control}
                name="licenceSeats"
                render={({ field, fieldState }) => (
                  <FormFieldInput
                    value={field.value ?? ''}
                    onChange={(e) => {
                      // Text, not type="number": a number input reports an
                      // unparseable entry ("1e", a lone "-") as an EMPTY value,
                      // which would clear a real cap on a typo. The raw string
                      // is kept instead, so zod refuses it and blocks the save.
                      const parsed = parseSeatLimit(e.target.value);
                      field.onChange(parsed.valid ? (parsed.limit ?? undefined) : e.target.value);
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    label={t('organization.systems.licenceSeatsLabel')}
                    placeholder={t('organization.systems.licenceSeatsPlaceholder')}
                    type="text"
                    inputMode="numeric"
                    error={fieldErrorMessage(fieldState.error)}
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}
    </FormDialog>
  );
}
